import {
  BasicTier,
  basicTierStripeMetadata,
  isSubscriptionTier,
  StripeRecurringInterval,
  stripeConsumerEndpointPrefix,
  subscriptionTierStripeMetadata,
  SubscriptionTier,
  UserFacingError
} from 'notability-services-common-universal'

export type {StripeRecurringInterval}

export type NotabilityPrice = {
  stripeId: string
  tier: SubscriptionTier
  intervalUnit: StripeRecurringInterval
  intervalCount: number
  centsPerInterval: number
  currency: string
  metadata: {
    [name: string]: string
  }
  portalUrl: string | null
}

// A one-time / lifetime purchase (e.g. Classic). Stripe returns these with `recurring: null`, so they
// carry an absolute amount rather than a per-interval one, and are tagged with a BasicTier value.
export type NotabilityOneTimePrice = {
  stripeId: string
  tier: BasicTier
  amountCents: number
  currency: string
  metadata: {
    [name: string]: string
  }
}

/**
 * Structural subset of `Stripe.Price` consumed by the mapper. Declared here (rather than importing
 * the `stripe` package into this shared frontend lib) so callers can pass a real `Stripe.Price`,
 * which satisfies this shape.
 */
type StripePriceLike = {
  id: string
  currency: string
  unit_amount: number | null
  metadata: {[name: string]: string}
  recurring: {interval: StripeRecurringInterval; interval_count: number} | null
}

/**
 * Number of months a transaction buys, for the Mixpanel `Subscription Months` property. Undefined
 * where a month count is meaningless — a one-time Classic purchase, or a day/week price — because
 * an explicitly-sent null would still register as a value and silently scope reports filtered on
 * this property.
 */
export function subscriptionMonthsForInterval(
  intervalUnit: StripeRecurringInterval | null | undefined,
  intervalCount: number | null | undefined
): number | undefined {
  if (!intervalUnit || !intervalCount) {
    return undefined
  }

  switch (intervalUnit) {
    case 'year':
      return intervalCount * 12
    case 'month':
      return intervalCount
    case 'day':
    case 'week':
      return undefined
  }
}

export function mapStripePricesToNotabilityPrices(
  stripePrices: StripePriceLike[],
  portalUrl: string | null
): NotabilityPrice[] {
  return stripePrices.flatMap((price) => {
    if (!price.recurring || !price.unit_amount) {
      return []
    }

    const tier = price.metadata[subscriptionTierStripeMetadata]
    if (!isSubscriptionTier(tier)) {
      return []
    }

    return [
      {
        stripeId: price.id,
        tier,
        intervalUnit: price.recurring.interval,
        intervalCount: price.recurring.interval_count,
        centsPerInterval: price.unit_amount,
        currency: price.currency,
        metadata: price.metadata,
        portalUrl
      }
    ]
  })
}

export function mapStripePricesToOneTimePrices(stripePrices: StripePriceLike[]): NotabilityOneTimePrice[] {
  return stripePrices.flatMap((price) => {
    if (price.recurring || !price.unit_amount) {
      return []
    }

    // Classic (lifetime) is the only recognized one-time consumer purchase.
    if (price.metadata[basicTierStripeMetadata] !== BasicTier.lifetime) {
      return []
    }

    return [
      {
        stripeId: price.id,
        tier: BasicTier.lifetime,
        amountCents: price.unit_amount,
        currency: price.currency,
        metadata: price.metadata
      }
    ]
  })
}

/**
 * Fetches the active consumer prices from the public (no-auth) `guestPrices` endpoint, split into
 * recurring subscription prices and one-time (e.g. Classic) prices. Used where there is no Stripe
 * customer context (e.g. the marketing pricing/referral pages and the login referral free-trial
 * disclaimer), so recurring prices carry a null `portalUrl`.
 */
export async function fetchGuestPrices(
  apiServiceUrl: string
): Promise<{prices: NotabilityPrice[]; oneTimePrices: NotabilityOneTimePrice[]}> {
  const res = await fetch(`${apiServiceUrl}${stripeConsumerEndpointPrefix}/guestPrices`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'}
  })

  if (!res.ok) {
    throw new UserFacingError('consumerNetworkError', {
      logData: {
        message: `Stripe guest prices endpoint gave not-ok status: ${res.status}/${res.statusText}`,
        url: res.url
      },
      statusCode: res.status
    })
  }

  const body: {prices?: {data?: StripePriceLike[]}} = await res.json()
  const stripePrices = body.prices?.data ?? []
  return {
    prices: mapStripePricesToNotabilityPrices(stripePrices, null),
    oneTimePrices: mapStripePricesToOneTimePrices(stripePrices)
  }
}
